import { ArrowLeftIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import {
  buildCriterionSummaries,
  countVerdicts,
  filterRowsForScorecardView,
  getScoringErrorSummary,
  normalizeCriterionSummaries,
  normalizeEvaluatorResults,
  toCellDisplayText,
  toSmartColumn,
} from "./evaluate-drawer-utils";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type {
  PlaygroundEvaluateColumn,
  PlaygroundEvaluateRunRow,
  PlaygroundEvaluateRunSummaryData,
  PlaygroundEvaluateSheetReference,
} from "@/queries/playground/evaluate";
import type {
  GetScorecardRowResponse,
  Scorecard,
  ScorecardRowSummary,
  ScorecardStaleState,
} from "@/queries/smart-tables/scorecard";
import type {
  ScorecardAllRowsListState,
  ScorecardCriterionRowsListState,
  ScorecardViewState,
} from "@/routes/smart-table/components/SmartTablePage/RightPanel/ScoreSidebarContent/Scorecard/types";
import ScorecardView from "@/routes/smart-table/components/SmartTablePage/RightPanel/ScoreSidebarContent/Scorecard/ScorecardView";
import ScorecardViewTransition, {
  getScorecardScreen,
} from "@/routes/smart-table/components/SmartTablePage/RightPanel/ScoreSidebarContent/Scorecard/ScorecardViewTransition";
import type { ScorecardDisplayCriterion } from "@/routes/smart-table/stores/scorecard-store";

interface EvaluatorBreakdownDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialView: Exclude<ScorecardViewState, { view: "overview" }>;
  rows: PlaygroundEvaluateRunRow[];
  runSummary?: PlaygroundEvaluateRunSummaryData | null;
  scorecardSnapshot?: Scorecard | null;
  sheetColumns: PlaygroundEvaluateColumn[];
  inputMappings: PlaygroundEvaluateSheetReference["input_variable_mappings"];
  templateOverrideColumnId?: string;
  staleStepIds: string[];
}

const EvaluatorBreakdownDialog = ({
  open,
  onOpenChange,
  initialView,
  rows,
  runSummary,
  scorecardSnapshot,
  sheetColumns,
  inputMappings,
  templateOverrideColumnId,
  staleStepIds,
}: EvaluatorBreakdownDialogProps) => {
  const [view, setView] = useState<ScorecardViewState>(initialView);
  const [allRowsListState, setAllRowsListState] = useState<ScorecardAllRowsListState>({
    search: "",
    filter: "all",
  });
  const [criterionRowsListState, setCriterionRowsListState] =
    useState<ScorecardCriterionRowsListState>({ search: "", filter: "all" });

  useEffect(() => {
    if (open) {
      setView(initialView);
    }
  }, [initialView, open]);

  const scorecard = scorecardSnapshot ?? null;
  const columns = sheetColumns.map(toSmartColumn);

  const getRowCellValue = useCallback(
    (rowIndex: number, columnId: string) => {
      const row = rows.find(r => r.row_index === rowIndex);
      if (!row) {
        return null;
      }

      if (templateOverrideColumnId === columnId) {
        if (typeof row.output_payload === "object" && row.output_payload !== null) {
          const outputPayload = row.output_payload as Record<string, unknown>;
          return toCellDisplayText(outputPayload.value ?? outputPayload.display_value);
        }
        return toCellDisplayText(row.output_payload);
      }

      const sourceValues = row.score_payload?.source_values;
      if (typeof sourceValues === "object" && sourceValues !== null) {
        const sourceValue = (sourceValues as Record<string, unknown>)[columnId];
        if (typeof sourceValue === "object" && sourceValue !== null) {
          const sourcePayload = sourceValue as Record<string, unknown>;
          if (sourcePayload.state === "missing") {
            return null;
          }
          if (sourcePayload.state === "blank") {
            return "Empty";
          }
          return toCellDisplayText(sourcePayload.display_value ?? sourcePayload.value);
        }
      }

      const mappingEntry = Object.entries(inputMappings).find(
        ([, mapping]) => mapping.column_id === columnId,
      );
      if (mappingEntry) {
        const variableName = mappingEntry[0];
        const val = row.input_variables?.[variableName];
        if (val !== undefined && val !== null) {
          return typeof val === "object" ? JSON.stringify(val) : String(val);
        }
      }

      return null;
    },
    [inputMappings, rows, templateOverrideColumnId],
  );

  const steps = scorecard?.steps ?? [];
  const staleStepIdSet = new Set(staleStepIds);
  const staleState: ScorecardStaleState | null =
    staleStepIds.length > 0
      ? {
          is_stale: true,
          reasons: ["Playground inputs changed evaluator dependencies"],
          stale_step_ids: staleStepIds,
          stale_row_indices: rows.map(row => row.row_index),
        }
      : null;
  const rowBreakdowns = Object.fromEntries(
    rows.map(row => [
      row.row_index,
      {
        success: true,
        row_index: row.row_index,
        calculation_id: "playground-evaluate-run",
        aggregate_score:
          typeof row.score_payload?.score === "number" ? Number(row.score_payload.score) : null,
        aggregate_verdict: String(row.score_payload?.verdict || row.status),
        step_results: normalizeEvaluatorResults(row.evaluator_breakdown),
        stale_state: staleState,
        error_summary: getScoringErrorSummary(row.score_payload),
      } satisfies GetScorecardRowResponse,
    ]),
  );
  const runCriterionSummaries = normalizeCriterionSummaries(runSummary?.criterion_summaries);
  const criterionSummaries =
    runCriterionSummaries.length > 0
      ? runCriterionSummaries
      : buildCriterionSummaries(steps, rowBreakdowns);
  const displayCriteria: ScorecardDisplayCriterion[] = steps.map(step => ({
    stepId: step.id,
    step,
    summary: criterionSummaries.find(summary => summary.step_id === step.id),
    isRemoved: false,
    primitiveType: step.primitive_type,
    title: step.title,
  }));
  const rowSummaries: ScorecardRowSummary[] = rows.map(row => ({
    row_index: row.row_index,
    aggregate_score:
      typeof row.score_payload?.score === "number" ? Number(row.score_payload.score) : null,
    aggregate_verdict: String(row.score_payload?.verdict || row.status),
    stale_state: staleState,
    error_summary: getScoringErrorSummary(row.score_payload),
  }));
  const visibleRows = filterRowsForScorecardView({
    allRowsListState,
    criterionRowsListState,
    rowBreakdowns,
    rowSummaries,
    view,
  });
  const rowsVerdictCounts = countVerdicts(rowSummaries.map(row => String(row.aggregate_verdict)));
  const criterionVerdictCounts =
    view.view === "criterion"
      ? countVerdicts(
          rowSummaries.map(row =>
            String(rowBreakdowns[row.row_index]?.step_results[view.stepId]?.verdict || "skipped"),
          ),
        )
      : undefined;
  const activeStep =
    view.view === "criterion" ? steps.find(step => step.id === view.stepId) : undefined;
  const activeSummary = activeStep
    ? criterionSummaries.find(summary => summary.step_id === activeStep.id)
    : undefined;
  const activeRowIndex = view.view === "row" ? view.rowIndex : undefined;

  const canGoBack = view.view === "row";
  let title = "Evaluator details";
  let description = "Inspect evaluator results and row-level details.";
  if (view.view === "all-rows") {
    title = "All rows";
    description = `${rowSummaries.length} evaluated input ${rowSummaries.length === 1 ? "set" : "sets"}`;
  } else if (view.view === "criterion") {
    title = activeStep?.title ?? "Evaluator details";
    description = "Evaluator performance across this run.";
  } else if (view.view === "row") {
    const sourceEvaluator = view.fromStepId
      ? steps.find(step => step.id === view.fromStepId)
      : undefined;
    title = `Input set ${view.rowIndex + 1}`;
    description = sourceEvaluator
      ? `Opened from ${sourceEvaluator.title}. Showing all evaluator results for this input.`
      : "All evaluator results and output for this input.";
  }
  const screen = getScorecardScreen(view, {
    isLoadingEmpty: false,
    hasScorecard: Boolean(scorecard),
  });
  const handleBack = () => {
    if (view.view === "row" && view.fromAllRows) {
      setView({ view: "all-rows" });
      return;
    }
    if (view.view === "row" && view.fromStepId) {
      setView({ view: "criterion", stepId: view.fromStepId });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-5xl gap-0 overflow-hidden p-0">
        <DialogHeader className="min-h-[4.25rem] border-b border-border/60 px-4 py-3">
          <div className="flex min-w-0 items-start gap-2 pr-8">
            {canGoBack ? (
              <Button
                type="button"
                variant="ghost"
                size="tinyIcon"
                className="-ml-1 mt-0.5 shrink-0 rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                onClick={handleBack}
                aria-label={
                  view.view === "row" && view.fromStepId
                    ? "Back to selected evaluator"
                    : "Back to all input sets"
                }
              >
                <ArrowLeftIcon className="h-4 w-4" />
              </Button>
            ) : null}
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate text-sm font-semibold leading-5 text-foreground">
                {title}
              </DialogTitle>
              <DialogDescription className="mt-0.5 line-clamp-2 text-xs leading-5 text-muted-foreground">
                {description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="max-h-[calc(88vh-4.25rem)] overflow-y-auto p-4">
          <ScorecardViewTransition screen={screen}>
            <ScorecardView
              isLoading={false}
              scorecard={scorecard}
              hasSimpleScore={false}
              configureIsPending={false}
              migrateLegacyIsPending={false}
              onCreateScorecard={() => undefined}
              onMigrateLegacyScorecard={() => undefined}
              view={view}
              rows={visibleRows}
              totalRows={rowSummaries.length}
              rowsVerdictCounts={rowsVerdictCounts}
              criterionVerdictCounts={criterionVerdictCounts}
              getRowLabel={rowIndex => `Input set ${rowIndex + 1}`}
              getRowCellValue={getRowCellValue}
              onOpenAllRows={filter => {
                setAllRowsListState(current => ({ ...current, filter: filter ?? current.filter }));
                setView({ view: "all-rows" });
              }}
              onOpenAllRowsRow={rowIndex => setView({ view: "row", rowIndex, fromAllRows: true })}
              onRerunRow={() => undefined}
              getIsRowRerunning={() => false}
              isRerunDisabled
              allRowsListState={allRowsListState}
              onAllRowsListStateChange={next =>
                setAllRowsListState(current => ({ ...current, ...next }))
              }
              criterionRowsListState={criterionRowsListState}
              onCriterionRowsListStateChange={next =>
                setCriterionRowsListState(current => ({ ...current, ...next }))
              }
              activeStep={activeStep}
              activeSummary={activeSummary}
              rowBreakdowns={rowBreakdowns}
              columns={columns}
              onOpenCriterionRow={rowIndex =>
                setView({ view: "row", rowIndex, fromStepId: activeStep?.id })
              }
              onEditCriterion={() => undefined}
              onRerunCriterionRows={() => undefined}
              fallbackRerunRowIndices={rowSummaries.map(row => row.row_index)}
              isCriterionRerunning={false}
              isActiveCriterionStale={Boolean(activeStep && staleStepIdSet.has(activeStep.id))}
              onSave={() => undefined}
              newCriterionDraftStep={null}
              activeRowIndex={activeRowIndex}
              activeRowBreakdown={
                activeRowIndex === undefined ? undefined : rowBreakdowns[activeRowIndex]
              }
              displayCriteria={displayCriteria}
              getIsCriterionRerunning={() => false}
              getIsCriterionStale={stepId => staleStepIdSet.has(stepId)}
              onRerunCriterion={() => undefined}
              onOpenFailedRows={(stepId, filter) => {
                setCriterionRowsListState({ search: "", filter: filter ?? "fail" });
                setView({ view: "criterion", stepId });
              }}
              onOpenCriterion={stepId => setView({ view: "criterion", stepId })}
              onAddRowCriterion={() => undefined}
              onSelectAddCriterion={() => undefined}
              onCloseAddCriterion={() => undefined}
              isReadOnly
            />
          </ScorecardViewTransition>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EvaluatorBreakdownDialog;
